import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FormulaResult } from "@/types/calculator";
import { Info, TrendingUp, TrendingDown } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface FormulaComparisonProps {
  formulaResults: FormulaResult[];
}

const FormulaComparison = ({ formulaResults }: FormulaComparisonProps) => {
  if (formulaResults.length <= 1) return null;

  const avgCalories = formulaResults.reduce((sum, r) => sum + r.caloriesPerSession, 0) / formulaResults.length;
  const maxDiff = Math.max(...formulaResults.map(r => Math.abs(r.caloriesPerSession - avgCalories)));
  const diffPercent = (maxDiff / avgCalories) * 100;

  return (
    <Card className="p-4 border-card-border rounded-2xl shadow-soft bg-secondary/30">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          Formula Comparison
          <Badge variant="outline" className="text-xs">
            {formulaResults.length} methods
          </Badge>
        </h3>
        <span className="text-xs text-muted-foreground">
          Max variance: ±{diffPercent.toFixed(1)}%
        </span>
      </div>

      <div className="space-y-2">
        {formulaResults.map((result) => {
          const diff = ((result.caloriesPerSession - avgCalories) / avgCalories) * 100;
          const isHigher = diff > 0;
          
          return (
            <div key={result.formula} className="bg-card p-3 rounded-xl border border-card-border">
              <div className="flex items-start justify-between mb-1">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm">{result.name}</span>
                    <Dialog>
                      <DialogTrigger asChild>
                        <button className="text-muted-foreground hover:text-foreground">
                          <Info className="w-3 h-3" />
                        </button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{result.name}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                          <p className="text-sm text-muted-foreground">{result.description}</p>
                          <div className="p-3 bg-secondary rounded-lg">
                            <p className="text-xs font-medium text-foreground mb-1">Citation:</p>
                            <p className="text-xs text-muted-foreground">{result.citation}</p>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-1">{result.description}</p>
                </div>
                <div className="text-right ml-2">
                  <p className="font-bold text-lg text-foreground">
                    {Math.round(result.caloriesPerSession)}
                  </p>
                  <div className="flex items-center gap-1 justify-end">
                    {isHigher ? (
                      <TrendingUp className="w-3 h-3 text-accent" />
                    ) : (
                      <TrendingDown className="w-3 h-3 text-primary" />
                    )}
                    <span className={`text-xs font-medium ${isHigher ? "text-accent" : "text-primary"}`}>
                      {isHigher ? "+" : ""}{diff.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 p-2 bg-muted/50 rounded-lg">
        <p className="text-xs text-muted-foreground text-center">
          <strong>Average:</strong> {Math.round(avgCalories)} kcal/session • Different formulas account for various factors
        </p>
      </div>
    </Card>
  );
};

export default FormulaComparison;
